import React, { useMemo } from "react";
import HealthCard from "./HealthCard";
import { formatNumber } from "../utils";

/**
 * fmd/27 shape:
 * {
 *   "now-30-days": { unexpectedPercentage, totalVehicles, remainingVehicles[] },
 *   "30-60-days":  { unexpectedPercentage, totalVehicles, remainingVehicles[] }
 * }
 * Rising breakdowns → red bar + ▲ ; falling → green bar + ▼
 */
const UnexpectedBreakdowns = ({ translate, data, loading }) => {
  const {
    currentPct,
    previousPct,
    delta,
    isIncreasing,
    visits,
    fleetSize,
    previousVisits,
  } = useMemo(() => {
    const current = data?.["now-30-days"] || data?.now30Days || {};
    const previous = data?.["30-60-days"] || data?.days30to60 || {};
    const curr = Number(current.unexpectedPercentage ?? 0);
    const prev = Number(previous.unexpectedPercentage ?? 0);
    const change = curr - prev;
    const currentVisits = Array.isArray(current.remainingVehicles)
      ? current.remainingVehicles.length
      : Number(current.visits ?? 0);
    const priorVisits = Array.isArray(previous.remainingVehicles)
      ? previous.remainingVehicles.length
      : Number(previous.visits ?? 0);

    return {
      currentPct: curr,
      previousPct: prev,
      delta: change,
      isIncreasing: change >= 0,
      visits: currentVisits,
      previousVisits: priorVisits,
      fleetSize: Number(current.totalVehicles ?? previous.totalVehicles ?? 0),
    };
  }, [data]);

  // Higher unexpected % is worse → red when rising, green when falling
  const barColor = isIncreasing ? "#e0484c" : "#5cb85c";
  const barWidth = Math.min(Math.max(Math.abs(delta), 0.5), 100);

  return (
    <HealthCard
      className="fh-span-3"
      title={translate("unexpectedBreakdowns")}
      subtitle={translate("unexpectedBreakdownsSub")}
      badge={`${isIncreasing ? "+" : "−"}${formatNumber(Math.abs(delta), 1)}%`}
      badgeStyle={
        isIncreasing
          ? { background: "#fdeaea", color: "#e0484c" }
          : { background: "#e8f5e8", color: "#3f9c40" }
      }
      loading={loading}
    >
      <div className="fh-kpi-visual fh-kpi-visual--stack">
        <div className="fh-kpi">
          {formatNumber(currentPct, 1)}
          <small>%</small>
        </div>
        <div
          className={`fh-delta ${isIncreasing ? "up" : "down"}`}
          style={{ color: barColor }}
        >
          {isIncreasing ? "▲" : "▼"} {formatNumber(Math.abs(delta), 1)}%{" "}
          {translate("vsPrior30Days") || "vs prior 30 days"}
        </div>
        <div className="fh-track" style={{ height: 6, marginTop: 10 }}>
          <div
            className="fh-fill"
            style={{
              width: `${barWidth}%`,
              background: barColor,
            }}
          />
        </div>
        <div className="fh-kpi-range">
          <span>
            {translate("prior30Days") || "30–60 days"}:{" "}
            {formatNumber(previousPct, 1)}%
          </span>
          <span>
            {translate("last30Days") || "Now–30 days"}:{" "}
            {formatNumber(currentPct, 1)}%
          </span>
        </div>
      </div>
      <div className="fh-kpi-meta">
        <div className="fh-metaline">
          <span>
            {translate("breakdownVisits")} ({translate("current")}/
            {translate("previous")})
          </span>
          <b>
            {formatNumber(visits)}
            <span style={{ color: "#8a97a3", fontWeight: 500 }}>
              {" "}
              / {formatNumber(previousVisits)}
            </span>
          </b>
        </div>
        <div className="fh-metaline">
          <span>{translate("fleetSize")}</span>
          <b>{formatNumber(fleetSize)}</b>
        </div>
      </div>
    </HealthCard>
  );
};

export default UnexpectedBreakdowns;
